/**
 * Wind direction arrow components — JSX replacements for the SVG strings that
 * legacy/js/utils.js returned via innerHTML.
 *
 * **Direction convention (post 2026-05-10):** the arrow tip points in the
 * direction the wind is *blowing toward*, i.e. away from its meteorological
 * source. So a "south wind" (`dirDeg = 180` — wind FROM the south) renders
 * with the tip pointing UP/north, the way the wind is actually moving. This
 * matches the spot-detail compass needle. The SVG glyph itself draws an
 * arrow pointing left along the −x axis; we rotate by `dirDeg − 90` so the
 * tip lands at `(dirDeg + 180)` from north.
 */

interface ArrowProps {
  /** Direction in degrees (0–360). */
  dirDeg: number;
  /** Override stroke colour. Falls through to `currentColor`, so callers
   *  can also colour via a parent `color: ...` instead of passing this. */
  color?: string;
}

interface DirArrowProps extends ArrowProps {
  /** Outer width/height in px. Defaults to 28 (the design-system size). */
  size?: number;
}

export function DirArrow({ dirDeg, color, size = 28 }: DirArrowProps) {
  const rot = dirDeg - 90;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 28 28"
      fill="none"
      style={{ transform: `rotate(${rot}deg)`, flexShrink: 0, ...(color && { color }) }}
      aria-hidden
    >
      <line x1="24" y1="14" x2="4" y2="14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <polyline
        points="10,8 4,14 10,20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface SmallArrowProps extends ArrowProps {
  /** Outer width/height in px. Defaults to 14. Figma's
   *  `--icon-size-sm` = 12 for the day-summary use; 14 reads as a
   *  slightly heavier accent in larger contexts. */
  size?: number;
}

export function SmallArrow({ dirDeg, color, size = 14 }: SmallArrowProps) {
  const rot = dirDeg - 90;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 14"
      fill="none"
      style={{ transform: `rotate(${rot}deg)`, flexShrink: 0, ...(color && { color }) }}
      aria-hidden
    >
      <line x1="11" y1="7" x2="3" y2="7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <polyline
        points="6,3 3,7 6,11"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
